// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useSnackbar } from 'notistack';
import React from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { useLedgerApiClient } from '../../contexts/LedgerApiContext';

const hasIndexedDBSize = (
  estimate: StorageEstimate,
): estimate is StorageEstimate & {
  usageDetails: { indexedDB: number };
} => {
  return (
    'usageDetails' in estimate &&
    typeof estimate.usageDetails === 'object' &&
    estimate.usageDetails !== null &&
    'indexedDB' in estimate.usageDetails &&
    typeof estimate.usageDetails.indexedDB === 'number'
  );
};

export const WebsocketClearCache: React.FC = () => {
  const ledgerApiClient = useLedgerApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = React.useState(false);
  const [indexedDBSize, setIndexedDBSize] = React.useState<number | null>(null);

  React.useEffect(() => {
    navigator.storage
      .estimate()
      .then((estimate) => {
        if (!hasIndexedDBSize(estimate)) {
          return;
        }

        setIndexedDBSize(estimate.usageDetails.indexedDB);
      })
      .catch((err: Error) => {
        console.error(`Failed to read storage estimates: ${err.message}.`);
      });
  }, []);

  const estimatedIndexedDBSize = React.useMemo(() => {
    if (!indexedDBSize) {
      return null;
    }

    const sizeInMB = (indexedDBSize / 1024 / 1024).toFixed(2);

    return <Typography variant="caption">Estimated taken space is: {sizeInMB}MB</Typography>;
  }, [indexedDBSize]);

  const onClose = () => {
    setOpen(false);
  };
  const onClear = () => {
    ledgerApiClient
      .clearAllActiveContractsStateCache()
      .then(() => {
        enqueueSnackbar('Cleared Active Contract store', {
          variant: 'success',
        });
      })
      .catch((err: Error) => {
        enqueueSnackbar(`Error clearing Active Contract store: ${err.message}`, {
          variant: 'error',
        });
      })
      .finally(() => {
        onClose();
      });
  };

  return (
    <>
      <Card
        sx={{ mb: 1, width: '50%', p: 1, display: 'flex', alignItems: 'center' }}
        variant="outlined"
      >
        <Box sx={{ mr: 1 }}>
          <Typography fontWeight="bold">Active Contract storage</Typography>
          <Typography variant="body2">
            Active contracts are accumulated in the Utility UI&apos;s storage via your browser.
            Clearing this storage will free up space.
          </Typography>
          {estimatedIndexedDBSize}
        </Box>
        <Button
          variant="outlined"
          sx={{ ml: 'auto' }}
          onClick={() => setOpen(true)}
          size="small"
          color="error"
        >
          Clear
        </Button>
      </Card>
      <Dialog open={open} onClose={onClose}>
        <DialogContent sx={{ maxWidth: '400px' }}>
          <Typography variant="body2">
            This will clear the Active Contract set stored in your browser.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Stack sx={{ width: '100%' }}>
            <Button fullWidth size="small" color="error" onClick={onClear}>
              Clear
            </Button>
            <Button sx={{ mt: 1 }} fullWidth size="small" variant="text" onClick={onClose}>
              Cancel
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
};
